import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { AvatarComponent } from "./blocks/avatar/avatar.component";
import { BadgeComponent } from "./blocks/badge/badge.component";
import { BoxHeaderComponent } from "./blocks/box/box-header/box-header.component";
import { BoxComponent } from "./blocks/box/box.component";
import { CountdownComponent } from "./blocks/countdown/countdown.component";
import { EmptyStateComponent } from "./blocks/empty-state/empty-state.component";
import { ExerciseStatusComponent } from "./blocks/exercise-status/exercise-status.component";
import { HourPickerComponent } from "./blocks/hour-picker/hour-picker.component";
import { IconCircleComponent } from "./blocks/icon-circle/icon-circle.component";
import { IconInputComponent } from "./blocks/icon-input/icon-input.component";
import { IndicatorComponent } from "./blocks/indicator/indicator.component";
import { ListProgressComponent } from "./blocks/list-progress/list-progress.component";
import { LoadingBoxComponent } from "./blocks/loading-box/loading-box.component";
import { CsLoaderComponent } from "./blocks/cs-loader/cs-loader.component";
import { PageHeaderComponent } from "./blocks/page-header/page-header.component";
import { PillComponent } from "./blocks/pill/pill.component";
import { PillsInputComponent } from "./blocks/pills-input/pills-input.component";
import { PillsComponent } from "./blocks/pills/pills.component";
import { PopupMenuComponent } from "./blocks/popup-menu/popup-menu.component";
import { RichTextComponent } from "./blocks/rich-text/rich-text.component";
import { RoundButtonComponent } from "./blocks/round-button/round-button.component";
import { SimpleElementComponent } from "./blocks/simple-element/simple-element.component";
import { SimpleListItemComponent } from "./blocks/simple-list-item/simple-list-item.component";
import { ValidationMessageComponent } from "./blocks/validation-message/validation-message.component";
import { ValidationTextComponent } from "./blocks/validation-text/validation-text.component";
import { CourseExerciseAnswerComponent } from "./components/course/course-exercise-answer/course-exercise-answer.component";
import { CourseExerciseDescComponent } from "./components/course/course-exercise-desc/course-exercise-desc.component";
import { CourseExerciseHelpComponent } from "./components/course/course-exercise-help/course-exercise-help.component";
import { CourseExerciseStatComponent } from "./components/course/course-exercise-stat/course-exercise-stat.component";
import { CourseExerciseListboxElementContentComponent } from "./components/course/course-exercise-listbox-element-content/course-exercise-listbox-element-content.component";
import { ComplexExerciseViewComponent } from "./components/course/complex-exercise-view/complex-exercise-view.component";
import { ComplexExerciseLoaderComponent } from "./components/course/complex-exercise-view/complex-exercise-loader/complex-exercise-loader.component";
import { ComplexSectionComponent } from "./components/course/complex-exercise-view/sections/complex-section.component";
import { CourseProgressTopicComponent } from "./components/course/course-progress-topic/course-progress-topic.component";
import { CourseProgressComponent } from "./components/course/course-progress/course-progress.component";
import { CourseTopicExercisesComponent } from "./components/course/course-topic-exercises/course-topic-exercises.component";
import { CourseTopicHeaderComponent } from "./components/course/course-topic-header/course-topic-header.component";
import { CourseTopicStatComponent } from "./components/course/course-topic-stat/course-topic-stat.component";
import { CourseTopicComponent } from "./components/course/course-topic/course-topic.component";
import { CourseListboxElementContentComponent } from "./components/course/course-listbox-element-content/course-listbox-element-content.component";
import { GroupInvitationListboxElementContentComponent } from "./components/group/group-invitation-listbox-element-content/group-invitation-listbox-element-content.component";
import { LanguagePickerComponent } from "./components/language-picker/language-picker.component";
import { ModalBoxComponent } from "./components/modal/modal-box/modal-box.component";
import { ModalOverlayComponent } from "./components/modal/modal-overlay/modal-overlay.component";
import { ModalComponent } from "./components/modal/modal.component";
import { ConfirmationModalComponent } from "./components/modals/confirmation-modal/confirmation-modal.component";
import { FileUploadConfirmationModalComponent } from "./components/modals/file-upload-confirmation-modal/file-upload-confirmation-modal.component";
import { VpnConfigurationModalComponent } from "./components/modals/vpn-configuration-modal/vpn-configuration-modal.component";
import { WarningModalComponent } from "./components/modals/warning-modal/warning-modal.component";
import { PartialGradeHistoryOverlayComponent } from "./components/student-stat-list-table/partial-grade-history-overlay/partial-grade-history-overlay.component";
import { NavbarComponent } from "./components/navbar-data/navbar/navbar.component";
import { NotificationsIconComponent } from "./components/notifications/notifications-icon/notifications-icon.component";
import { NotificationComponent } from "./components/notifications/notifications-panel/notification.component";
import { NotificationsPanelComponent } from "./components/notifications/notifications-panel/notifications-panel.component";
import { PageColumnLayoutComponent } from "./components/page-column-layout/page-column-layout.component";
import { PageStandardLayoutComponent } from "./components/page-standard-layout/page-standard-layout.component";
import { ConfirmationDirective } from "./directives/confirmation/confirmation.directive";
import { HighlightDirective } from "./directives/highlight/highlight.directive";
import { PopupDirective } from "./directives/popup/popup.directive";
import { WarningModalDirective } from "./directives/warning-modal/warning-modal.directive";
import { HttpLoaderFactory } from "./factories/HttpLoaderFactory";
import { PanelPageComponent } from "./pages/panel-page/panel-page.component";
import { DayPipe } from "./pipes/day.pipe";
import { FormatHourPipe } from "./pipes/format-hour.pipe";
import { ForumReactionEmojiPipe } from "./pipes/forum-reaction-emoji.pipe";
import { InvitationStatusPipe } from "./pipes/invitation-status.pipe";
import { InitialsPipe } from "./pipes/initials.pipe";
import { IterationPipe } from "./pipes/iteration.pipe";
import { ReplaceNewLinePipe } from "./pipes/replace-new-line.pipe";
import { SanitizeContentPipe } from "./pipes/sanitize-content.pipe";
import { NotificationToastComponent } from "./components/notifications/notification-toast.component";
import { DateDifferencePipe } from "./pipes/date-difference.pipe";
import { RelativeTimePipe } from "./pipes/relative-time.pipe";
import { WelcomeScreenModalComponent } from "./components/modals/welcome-screen-modal/welcome-screen-modal.component";
import { GlobalRankingPageComponent } from "./pages/global-ranking-page/global-ranking-page.component";
import { FluidTableComponent } from "./components/fluid-table/fluid-table.component";
import { RankingTableComponent } from "./components/ranking-table/ranking-table.component";
import { RankingFiltersComponent } from "./components/ranking-filters/ranking-filters.component";
import { RankingRefreshComponent } from "./components/ranking-refresh/ranking-refresh.component";
import { RecaptchaInfoComponent } from "./components/recaptcha-info/recaptcha-info.component";
import { ExerciseControlButtonsComponent } from "./components/course/exercise-control-buttons/exercise-control-buttons.component";
import { ExerciseTimerComponent } from "./components/exercise-timer/exercise-timer.component";
import { FooterComponent } from "./components/footer/footer.component";
import { ExerciseSolveConfirmModalComponent } from "./components/modals/exercise-solve-confirm-modal/exercise-solve-confirm-modal.component";
import { PointsPluralizationPipe } from "./pipes/points-pluralization.pipe";
import { PluralKeyPipe } from "./pipes/plural-key.pipe";
import { StatusIconComponent } from "./blocks/status-icon/status-icon.component";
import { DropdownMenuComponent } from "./blocks/dropdown-menu/dropdown-menu.component";
import { NavbarExerciseTimerComponent } from "./components/navbar-data/blocks/navbar-exercise-timer/navbar-exercise-timer.component";
import { NavbarBreadcrumbsComponent } from "./components/navbar-data/blocks/navbar-breadcrumbs/navbar-breadcrumbs.component";
import { QuizSolveConfirmModalComponent } from "./components/modals/quiz-solve-confirm-modal/quiz-solve-confirm-modal.component";
import { CookieBarComponent } from "./components/cookie-bar/cookie-bar.component";
import { NotificationsSnoozeComponent } from "./components/notifications/notifications-snooze/notifications-snooze.component";
import { NotificationsSettingsComponent } from "./components/notifications/notifications-settings/notifications-settings.component";
import { SettingGroupComponent } from "./components/notifications/notifications-settings/setting-group.component";
import { SnoozeGroupComponent } from "./components/notifications/notifications-snooze/snooze-group.component";
import { ExerciseHistoryButtonComponent } from "./components/course/exercise-history-button/exercise-history-button.component";
import { CodeCheckHistoryDialogComponent } from "./components/modals/code-check-history-dialog/code-check-history-dialog.component";
import { CodeCheckStatusPipe } from "./pipes/code-check-status.pipe";
import { MonacoEditorModule } from "ngx-monaco-editor";
import { DatePipe } from "./pipes/date.pipe";
import { RedeemModalComponent } from "./components/modals/redeem-modal/redeem-modal.component";
import { GroupReedemListboxElementContentComponent } from "./components/group/group-reedem-listbox-element-content/group-reedem-listbox-element-content.component";
import { SurpriseBoxComponent } from "./components/surprise-box/surprise-box.component";
import { ListPickerComponent } from "./components/list-picker/list-picker.component";
import { SliderPickerComponent } from "./components/slider-picker/slider-picker.component";
import { ProgressRingComponent } from "./blocks/progress-ring/progress-ring.component";
import { MoveStudentModalComponent } from "./components/modals/move-student-modal/move-student-modal.component";
import { PaginationComponent } from "./components/pagination/pagination.component";
import { OrderByAvailabilityPipe } from "./pipes/order-by-availability.pipe";
import { NumberedPipe } from "./pipes/numbered.pipe";
import { AnnouncementComponent } from "./components/announcement/announcement.component";
import { PanelAnnouncementBarComponent } from "./components/panel-announcement-bar/panel-announcement-bar.component";
import { SwitchButtonComponent } from "./blocks/switch-button/switch-button.component";
import { TooltipDirective } from "./directives/tooltip/tooltip.directive";
import { TooltipComponent } from "./blocks/tooltip/tooltip.component";
import { FileSizePipe } from "./pipes/file-size.pipe";
import { GroupStudentMoveListboxElementContentComponent } from "./components/group/group-student-move-listbox-content-element/group-student-move-listbox-element-content.component";
import { QuizHistoryDialogComponent } from "./components/modals/quiz-history-dialog/quiz-history-dialog.component";
import { WidgetDirective } from "./directives/widget/widget.directive";
import { OrganizationLimitWidgetComponent } from "./components/widgets/organization-limit-widget/organization-limit-widget.component";
import { TileComponent } from "./blocks/tile/tile.component";
import { CarouselComponent } from "./components/carousel/carousel.component";
import { DashboardLayoutComponent } from "./components/dashboard-layout/dashboard-layout.component";
import { DashboardSidebarComponent } from "./components/dashboard-sidebar/dashboard-sidebar.component";
import { SwitchButtonExpandedComponent } from "./blocks/switch-button-expanded/switch-button-expanded.component";
import { SwitchCheckboxComponent } from "./blocks/switch-checkbox/switch-checkbox.component";
import { EnvironmentTranslatePipe } from "./pipes/environment-translate.pipe";
import { HistoryModalHeaderComponent } from "./blocks/history-modal-blocks/history-modal-header/history-modal-header.component";
import { CodeHistoryItemListComponent } from "./blocks/history-modal-blocks/code-history-item-list/code-history-item-list.component";
import { QuizHistoryItemListComponent } from "./blocks/history-modal-blocks/quiz-history-item-list/quiz-history-item-list.component";
import { QuizHistoryModalItemComponent } from "./components/modals/quiz-history-modal/quiz-history-modal-item/quiz-history-modal-item.component";
import { CodeCheckHistoryModalItemComponent } from "./components/modals/code-check-history-modal/code-check-history-modal-item/code-check-history-modal-item.component";
import { CodeCheckTypePipe } from "./pipes/code-check-type.pipe";
import { DotsComponent } from "./blocks/dots/dots.component";
import { VpnConnectionModalComponent } from "./components/modals/vpn-connection-modal/vpn-connection-modal.component";
import { InvitationsWidgetComponent } from "./components/invitations-widget/invitations-widget.component";
import { CustomVideoPlayerComponent } from "./components/custom-video-player/custom-video-player.component";
import { ValidationHintComponent } from "./blocks/validation-hint/validation-hint.component";
import { HoverDirective } from "./directives/hover/hover.directive";
import { LanguagePickerButtonWithFlagLayoutComponent } from "./components/language-picker/language-picker-button-with-flag-layout/language-picker-button-with-flag-layout.component";
import { LanguagePickerLangsWithFlagLayoutComponent } from "./components/language-picker/language-picker-langs-with-flag-layout/language-picker-langs-with-flag-layout.component";
import { DefaultLanguagePickerButtonLayoutComponent } from "./components/language-picker/default-language-picker-button-layout/default-language-picker-button-layout.component";
import { DefaultLanguagePickerLangsLayoutComponent } from "./components/language-picker/default-language-picker-langs-layout/default-language-picker-langs-layout.component";
import { GroupTeacherNameComponent } from "./components/group/group-teacher-name/group-teacher-name.component";
import { NavbarExitExerciseButtonComponent } from "./components/navbar-data/blocks/navbar-exit-exercise-button/navbar-exit-exercise-button.component";
import { ExerciseIdeControlButtonsComponent } from "./components/course/exercise-ide-control-buttons/exercise-ide-control-buttons.component";
import { ExerciseQuizControlButtonsComponent } from "./components/course/exercise-quiz-control-buttons/exercise-quiz-control-buttons.component";
import { ExerciseNoneControlButtonsComponent } from "./components/course/exercise-none-control-buttons/exercise-none-control-buttons.component";
import { CustomVideoPlayerDirective } from "./directives/custom-video-player/custom-video-player.directive";
import { SectionTitleComponent } from "./blocks/section-title/section-title.component";
import { GroupListboxElementContentComponent } from "./components/group/group-listbox-element-content/group-listbox-element-content.component";
import { DividerComponent } from "./blocks/divider/divider.component";
import { ListboxComponent } from "./blocks/listbox/listbox.component";
import { ListboxElementComponent } from "./blocks/listbox-element/listbox-element.component";
import { StudentsCountBadgeComponent } from "./blocks/students-count-badge/students-count-badge.component";
import { ResolvedExercisesCountBadgeComponent } from "./blocks/resolved-exercises-count-badge/resolved-exercises-count-badge.component";
import { LogoComponent } from "./components/logo/logo.component";
import { CourseExerciseDifficultyComponent } from "./components/course/course-exercise-difficulty/course-exercise-difficulty.component";
import { ExerciseStatusCssClassPipe } from "./pipes/exercise-status-css-class.pipe";
import { GroupListboxEmptyComponent } from "./components/group/group-listbox-empty/group-listbox-empty.component";
import { ListboxEmptyComponent } from "./blocks/listbox-empty/listbox-empty.component";
import { GroupInvitationListboxEmptyComponent } from "./components/group/group-invitation-listbox-empty/group-invitation-listbox-empty.component";
import { CourseListboxEmptyComponent } from "./components/course/course-listbox-empty/course-listbox-empty.component";
import { OrganizationLimitWidgetCarouselContentComponent } from "./components/widgets/organization-limit-widget/organization-limit-widget-carousel-content/organization-limit-widget-carousel-content.component";
import { KeyIconComponent } from "./blocks/key-icon/key-icon.component";
import { PopupTriggerLabelWithChevronComponent } from "./blocks/popup-trigger-label-with-chevron/popup-trigger-label-with-chevron.component";
import { IconComponent } from "./blocks/icon/icon.component";
import { SelectWithPlaceholderComponent } from "./blocks/select-with-placeholder/select-with-placeholder.component";
import { SimpleDashboardWidgetComponent } from "./components/widgets/simple-dashboard-widget/simple-dashboard-widget.component";
import { GroupWidgetComponent } from "./components/widgets/group-widget/group-widget.component";
import { CourseGroupForumCardComponent } from "./components/course/course-group-forum-card/course-group-forum-card.component";
import { CourseGroupInfoComponent } from "./components/course/course-group-info/course-group-info.component";
import { CourseGroupListHeaderComponent } from "./components/course/course-group-list-header/course-group-list-header.component";
import { CourseGroupListActionsComponent } from "./components/course/course-group-list-actions/course-group-list-actions.component";
import { CourseGroupListEmptyComponent } from "./components/course/course-group-list-empty/course-group-list-empty.component";
import { RoundButtonClassPipe } from "./blocks/round-button/pipes/round-button-class.pipe";
import { NoDataPipe } from "./pipes/no-data.pipe";
import { DropdownComponent } from "./blocks/dropdown/dropdown.component";
import { DropdownItemComponent } from "./blocks/dropdown/components/dropdown-item/dropdown-item.component";
import { ClickOutsideDirective } from "./directives/click/click-outside.directive";
import { TabViewComponent } from "./blocks/tab-view/tab-view.component";
import { TabPanelComponent } from "./blocks/tab-view/components/tab-panel/tab-panel.component";
import { SelectButtonComponent } from "./blocks/select-button/select-button.component";
import { InputSwitchComponent } from "./blocks/input-switch/input-switch.component";
import { InputNumberComponent } from "./blocks/input-number/input-number.component";
import { StudentPassFilterFormComponent } from "../modules/teacher/components/student-pass-filter-form/student-pass-filter-form.component";
import { StudentPassCriteriaListComponent } from "../modules/teacher/components/student-pass-criteria-list/student-pass-criteria-list.component";
import { StudentExerciseCellClassPipe } from "./components/student-stat-list-table/pipes/student-exercise-cell-class.pipe";
import { StudentExerciseGradeCellClassPipe } from "./components/student-stat-list-table/pipes/student-exercise-grade-cell-class.pipe";
import { StudentExerciseGradePipe } from "./components/student-stat-list-table/pipes/student-exercise-grade.pipe";
import { StudentExercisePercentageCellClassPipe } from "./components/student-stat-list-table/pipes/student-exercise-percentage-cell-class.pipe";
import { StudentExercisePercentagePipe } from "./components/student-stat-list-table/pipes/student-exercise-percentage.pipe";
import { StudentStatListTableComponent } from "./components/student-stat-list-table/student-stat-list-table.component";
import { OperatorLiteralPipe } from "./pipes/operator-literal.pipe";
import { HintComponent } from "./blocks/hint/hint.component";
import { NgxMaskModule } from "ngx-mask";
import { CourseExerciseFileUploadModalComponent } from "./components/modals/course-exercise-file-upload-modal/course-exercise-file-upload-modal.component";
import { FileListComponent } from "./blocks/file-upload/components/file-list/file-list.component";
import { ProgressBarComponent } from "./blocks/progress-bar/progress-bar.component";
import { FileUploadComponent } from "./blocks/file-upload/file-upload.component";
import { FormatExtensionsPipe } from "./pipes/format-extensions.pipe";
import { CourseExerciseAnswerTableComponent } from "./components/course/course-exercise-answer-table/course-exercise-answer-table.component";
import { CourseExerciseAnswerMenuComponent } from "./components/course/course-exercise-answer-menu/course-exercise-answer-menu.component";
import { ExerciseFileUploadControlButtonsComponent } from "./components/course/exercise-file-upload-control-buttons/exercise-file-upload-control-buttons.component";
import { RoundBadgeComponent } from "./blocks/round-badge/round-badge.component";
import { CourseExerciseStudentUploadComponent } from "./components/course/course-exercise-student-upload/course-exercise-student-upload.component";
import { CourseExerciseAnswerListModalComponent } from "./components/modals/course-exercise-answer-list-modal/course-exercise-answer-list-modal.component";
import { AnswerStatusComponent } from "./blocks/answer-status/answer-status.component";
import { OrganizationSelectorComponent } from "./components/organization-selector/organization-selector.component";
import { OrganizationNamePipe } from "./pipes/organization-name.pipe";
import { CourseLimitWidgetComponent } from "./components/widgets/course-limit-widget/course-limit-widget.component";
import { PageToolbarComponent } from "./components/page-toolbar/page-toolbar.component";
import { OverlayModule } from "@angular/cdk/overlay";
import { CardCourseComponent } from "./components/card-course/card-course.component";
import { TeacherDashboardGroupListboxElementContentComponent } from "./components/teacher-dashboard-group-listbox-element-content/teacher-dashboard-group-listbox-element-content.component";
import { TableComponent } from "./components/table/table.component";
import { CircleBadgeComponent } from "./blocks/circle-badge/circle-badge.component";
import { ShortNamePipe } from "./pipes/short-name.pipe";
import { StatusChipComponent } from "./blocks/status-chip/status-chip.component";
import { ColorStatusPipe } from "./pipes/color-status.pipe";
import { ExpansionPanelComponent } from "./components/expansion-panel/expansion-panel.component";
import { HighlightComponent } from "./blocks/highlight/highlight.component";
import { ArrowPositionPipe } from "./pipes/arrow-position.pipe";
import { PartialGradeBarComponent } from "./components/partial-grade-bar/partial-grade-bar.component";
import { CardCertificateComponent } from "./components/card-certificate/card-certificate.component";
import { StudentExerciseTotalPointsPipe } from "./components/student-stat-list-table/pipes/student-exercise-total-points.pipe";
import { IconStatusPipe } from "./pipes/icon-status.pipe";
import { ExercisesStatListTableComponent } from "./components/exercises-stat-list-table/exercises-stat-list-table.component";
import { FlagExerciseBtnComponent } from "./components/flag-exercise-btn/flag-exercise-btn.component";
import { FlaggedExercisesListComponent } from "./components/flagged-exercises-list/flagged-exercises-list.component";
import { RingProgressBarComponent } from "./components/ring-progress-bar/ring-progress-bar.component";
import { CourseQuizSettingsModalComponent } from "./components/modals/course-quiz-settings-modal/course-quiz-settings-modal.component";
import { StudentFlaggedExercisesListTableComponent } from "./components/student-flagged-exercises-list-table/student-flagged-exercises-list-table.component";
import { ExerciseResolveComponent } from "./components/exercise-resolve/exercise-resolve.component";
import { BooleanToStringPipe } from "./pipes/boolean-to-string.pipe";
import { CriteriaBarComponent } from "./components/criteria-bar/criteria-bar.component";
import { ConfirmButtonsComponent } from "./blocks/confirm-buttons/confirm-buttons.component";
import { QuizInfoStudentModalComponent } from "./components/modals/quiz-info-student-modal/quiz-info-student-modal.component";
import { DisableWheelDirective } from "./directives/disable-wheel/disable-wheel.directive";
import { ExerciseHelpRequestFormComponent } from "./components/course/exercise-help-request-form/exercise-help-request-form.component";
import { ExerciseHelpRequestActionsComponent } from "./components/course/exercise-help-request-actions/exercise-help-request-actions.component";
import { ExerciseHelpRequestModalComponent } from "./components/modals/exercise-help-request-modal/exercise-help-request-modal.component";
import { ConsultationTimeComponent } from "./components/consultation-time/consultation-time.component";
import { AttendanceWidgetComponent } from "./components/attendance-widget/attendance-widget.component";
import { StreakBadgeComponent } from "./components/streak-badge/streak-badge.component";
import { AttendanceCalendarComponent } from "src/app/modules/teacher/components/attendance-calendar/attendance-calendar.component";
import { AttendanceCheckModalComponent } from "src/app/modules/teacher/components/attendance-check-modal/attendance-check-modal.component";
import { AttendanceDeleteModalComponent } from "src/app/modules/teacher/components/attendance-delete-modal/attendance-delete-modal.component";
import { AttendanceHistoryComponent } from "src/app/modules/teacher/components/attendance-history/attendance-history.component";
import { AttendanceRecordHistoryModalComponent } from "src/app/modules/teacher/components/attendance-record-history-modal/attendance-record-history-modal.component";
import { CourseGroupsComponent } from "./components/course/course-groups/course-groups.component";
import { GroupDataFormComponent } from "./components/group-data-form/group-data-form.component";
import { DatepickerComponent } from "./components/datepicker/datepicker.component";
import { FileUploadCommentModalComponent } from "./components/modals/file-upload-comment-modal/file-upload-comment-modal.component";
import { ExerciseTextControlButtonsComponent } from "./components/course/exercise-text-control-buttons/exercise-text-control-buttons.component";
import { ExerciseComplexControlButtonsComponent } from "./components/course/exercise-complex-control-buttons/exercise-complex-control-buttons.component";
import { ExerciseTextAnswerFormComponent } from "./components/course/exercise-text-answer-form/exercise-text-answer-form.component";
import { CourseExerciseAnswerHistoryModalComponent } from "../modules/student/components/student-text-answers-history-modal/course-exercise-answer-history-modal.component";
import { ShortTextCommentModalComponent } from "./components/modals/short-text-comment-modal/short-text-comment-modal.component";
import { CarouselBannerComponent } from "./components/carousel-banner/carousel-banner.component";
import { SnackbarComponent } from "./components/snackbar/snackbar.component";
import { DisplayNamePipe } from "./pipes/display-name.pipe";
import { ScheduleModalComponent } from "./components/modals/access-topic-modal/schedule-modal.component";
import { DownloadFormatPanelComponent } from "./components/download-format-panel/download-format-panel.component";
import { ConvertCodeText } from "./pipes/convert-code-text.pipe";
import { RegulationsModalComponent } from "./components/modals/regulations-modal/regulations-modal.component";
import { FeedbackWidgetComponent } from "./components/feedback-widget/feedback-widget.component";
import { RatingStarsComponent } from "./blocks/rating-stars/rating-stars.component";
import { FeedbackCourseModalComponent } from "./components/modals/feedback-course-modal/feedback-course-modal.component";
import { CourseGradingExercisesComponent } from "./components/course/course-grading-exercises/course-grading-exercises.component";
import { NetworkStatusComponent } from "./blocks/network-status/network-status.component";
import { CourseBlockElementContentComponent } from "./blocks/course-block-element-content/course-block-element-content.component";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NavbarMobileComponent } from "./components/navbar-data/navbar-mobile/navbar-mobile.component";
import { NavbarMobileElementComponent } from "./components/navbar-data/blocks/navbar-mobile-element/navbar-mobile-element.component";
import { LangSwitcherComponent } from "./components/lang-switcher/lang-switcher.component";
import { SidebarComponent } from "./components/navbar-data/sidebar/sidebar.component";
import { CourseGroupLoadMoreComponent } from "./components/course-group-load-more/course-group-load-more.component";
import { PanelNavbarComponent } from "./pages/panel-navbar/panel-navbar.component";
import { LucideAngularModule } from "lucide-angular";
import { TablerIconsModule } from "angular-tabler-icons";
import { AuthLayoutComponent } from "./pages/auth-layout/auth-layout.component";
import { SkeletonPageLoaderComponent } from "./components/skeleton-loaders/skeleton-page-loader/skeleton-page-loader.component";
import { SkeletonHeaderLoaderComponent } from "./components/skeleton-loaders/skeleton-header-loader/skeleton-header-loader.component";
import { SkeletonRowsLoaderComponent } from "./components/skeleton-loaders/skeleton-rows-loader/skeleton-rows-loader.component";
import { SkeletonForumUsersLoaderComponent } from "./components/skeleton-loaders/skeleton-forum-users-loader/skeleton-forum-users-loader.component";
import { SkeletonAttachmentButtonsLoaderComponent } from "./components/skeleton-loaders/skeleton-attachment-buttons-loader/skeleton-attachment-buttons-loader.component";
import { SkeletonChatUsersLoaderComponent } from "./components/skeleton-loaders/skeleton-chat-users-loader/skeleton-chat-users-loader.component";
import { SkeletonChatConversationLoaderComponent } from "./components/skeleton-loaders/skeleton-chat-conversation-loader/skeleton-chat-conversation-loader.component";
import { SkeletonMoveGroupsLoaderComponent } from "./components/skeleton-loaders/skeleton-move-groups-loader/skeleton-move-groups-loader.component";
import { UserProfileMenuComponent } from "./components/user-profile-menu/user-profile-menu.component";
import { ViewSwitcherComponent } from "./components/view-switcher/view-switcher.component";
import { CourseExerciseHistoryDataTableComponent } from "./components/course/course-tables/course-exercise-history-data-table/course-exercise-history-data-table.component";
import { CourseExerciseFileUploadDataTableComponent } from "./components/course/course-tables/course-exercise-file-upload-data-table/course-exercise-file-upload-data-table.component";
import { CourseExerciseShortTextDataTableComponent } from "./components/course/course-tables/course-exercise-short-text-data-table/course-exercise-short-text-data-table.component";
import { CourseTeacherShortTextAnswersTableComponent } from "./components/course/course-teacher-short-text-answers-table/course-teacher-short-text-answers-table.component";
import { CourseTeacherFileUploadAnswersTableComponent } from "./components/course/course-teacher-file-upload-answers-table/course-teacher-file-upload-answers-table.component";
import { CourseTeacherComplexAnswersTableComponent } from "./components/course/course-teacher-complex-answers-table/course-teacher-complex-answers-table.component";
import { ComplexAnswersLoaderComponent } from "./components/course/course-teacher-complex-answers-table/complex-answers-loader/complex-answers-loader.component";
import { ExerciseFileUploadInlineFormComponent } from "./components/course/exercise-file-upload-inline-form/exercise-file-upload-inline-form.component";
import { SquareButtonComponent } from "./blocks/square-button/square-button.component";
import { MobileTextAnswerHistoryComponent } from "./components/course/mobile-text-answer-history/mobile-text-answer-history.component";
import { MobileFileUploadAnswerHistoryComponent } from "./components/course/mobile-file-upload-answer-history/mobile-file-upload-answer-history.component";
import { MobileQuizStartComponent } from "./components/course/mobile-quiz-start/mobile-quiz-start.component";
import { MobileHistoryTableComponent } from "./components/course/mobile-history-table/mobile-history-table.component";
import { LUCIDE_SHARED_ICONS, TABLER_SHARED_ICONS } from "./icons/shared-icons";
import { CoursePdfReaderComponent } from "./components/course/course-pdf-reader/course-pdf-reader.component";

@NgModule({
  declarations: [
    PanelPageComponent,
    IconInputComponent,
    IconCircleComponent,
    NavbarComponent,
    AvatarComponent,
    PageHeaderComponent,
    PageStandardLayoutComponent,
    CourseListboxElementContentComponent,
    ListProgressComponent,
    RichTextComponent,
    CourseProgressComponent,
    PageColumnLayoutComponent,
    BoxHeaderComponent,
    CourseProgressTopicComponent,
    BoxComponent,
    RoundButtonComponent,
    CourseTopicComponent,
    CourseTopicHeaderComponent,
    CourseTopicExercisesComponent,
    CourseExerciseListboxElementContentComponent,
    ComplexExerciseViewComponent,
    ComplexExerciseLoaderComponent,
    ComplexSectionComponent,
    CourseExerciseHelpComponent,
    CourseExerciseDescComponent,
    BadgeComponent,
    CourseExerciseAnswerComponent,
    WarningModalComponent,
    WarningModalDirective,
    ValidationMessageComponent,
    ValidationTextComponent,
    LoadingBoxComponent,
    ReplaceNewLinePipe,
    LanguagePickerComponent,
    CountdownComponent,
    GroupInvitationListboxElementContentComponent,
    HourPickerComponent,
    PillsInputComponent,
    PillComponent,
    PillsComponent,
    SimpleListItemComponent,
    SimpleElementComponent,
    ExerciseStatusComponent,
    CourseExerciseStatComponent,
    CourseTopicStatComponent,
    HighlightDirective,
    IndicatorComponent,
    PopupMenuComponent,
    DayPipe,
    FormatHourPipe,
    ForumReactionEmojiPipe,
    ModalComponent,
    ModalOverlayComponent,
    ModalBoxComponent,
    ConfirmationModalComponent,
    PartialGradeHistoryOverlayComponent,
    FileUploadConfirmationModalComponent,
    ConfirmationDirective,
    InvitationStatusPipe,
    InitialsPipe,
    SanitizeContentPipe,
    IterationPipe,
    PopupDirective,
    VpnConfigurationModalComponent,
    NotificationsIconComponent,
    NotificationsPanelComponent,
    NotificationComponent,
    NotificationToastComponent,
    DateDifferencePipe,
    RelativeTimePipe,
    WelcomeScreenModalComponent,
    GlobalRankingPageComponent,
    FluidTableComponent,
    RankingTableComponent,
    RankingFiltersComponent,
    RankingRefreshComponent,
    RecaptchaInfoComponent,
    ExerciseControlButtonsComponent,
    ExerciseTimerComponent,
    FooterComponent,
    ExerciseSolveConfirmModalComponent,
    PointsPluralizationPipe,
    PluralKeyPipe,
    StatusIconComponent,
    DropdownMenuComponent,
    NavbarExerciseTimerComponent,
    NavbarBreadcrumbsComponent,
    QuizSolveConfirmModalComponent,
    CookieBarComponent,
    NotificationsSnoozeComponent,
    NotificationsSettingsComponent,
    SettingGroupComponent,
    SnoozeGroupComponent,
    ExerciseHistoryButtonComponent,
    CodeCheckHistoryDialogComponent,
    CodeCheckHistoryDialogComponent,
    CodeCheckStatusPipe,
    DatePipe,
    RedeemModalComponent,
    GroupReedemListboxElementContentComponent,
    SurpriseBoxComponent,
    ListPickerComponent,
    SliderPickerComponent,
    ProgressRingComponent,
    PaginationComponent,
    OrderByAvailabilityPipe,
    NumberedPipe,
    AnnouncementComponent,
    PanelAnnouncementBarComponent,
    SwitchButtonComponent,
    TooltipDirective,
    TooltipComponent,
    FileSizePipe,
    WidgetDirective,
    MoveStudentModalComponent,
    GroupStudentMoveListboxElementContentComponent,
    QuizHistoryDialogComponent,
    OrganizationLimitWidgetComponent,
    TileComponent,
    CarouselComponent,
    DashboardLayoutComponent,
    DashboardSidebarComponent,
    SwitchButtonExpandedComponent,
    SwitchCheckboxComponent,
    EnvironmentTranslatePipe,
    HistoryModalHeaderComponent,
    CodeHistoryItemListComponent,
    QuizHistoryItemListComponent,
    QuizHistoryModalItemComponent,
    CodeCheckHistoryModalItemComponent,
    CodeCheckTypePipe,
    DotsComponent,
    VpnConnectionModalComponent,
    InvitationsWidgetComponent,
    CustomVideoPlayerComponent,
    ValidationHintComponent,
    EmptyStateComponent,
    GroupTeacherNameComponent,
    HoverDirective,
    LanguagePickerButtonWithFlagLayoutComponent,
    LanguagePickerLangsWithFlagLayoutComponent,
    DefaultLanguagePickerButtonLayoutComponent,
    DefaultLanguagePickerLangsLayoutComponent,
    NavbarExitExerciseButtonComponent,
    ExerciseIdeControlButtonsComponent,
    ExerciseQuizControlButtonsComponent,
    ExerciseNoneControlButtonsComponent,
    CustomVideoPlayerDirective,
    GroupListboxElementContentComponent,
    DividerComponent,
    SectionTitleComponent,
    ListboxComponent,
    ListboxElementComponent,
    StudentsCountBadgeComponent,
    ResolvedExercisesCountBadgeComponent,
    LogoComponent,
    CourseExerciseDifficultyComponent,
    ExerciseStatusCssClassPipe,
    ListboxEmptyComponent,
    GroupListboxEmptyComponent,
    GroupInvitationListboxEmptyComponent,
    CourseListboxEmptyComponent,
    OrganizationLimitWidgetCarouselContentComponent,
    KeyIconComponent,
    PopupTriggerLabelWithChevronComponent,
    IconComponent,
    SelectWithPlaceholderComponent,
    SimpleDashboardWidgetComponent,
    GroupWidgetComponent,
    CourseGroupForumCardComponent,
    CourseGroupInfoComponent,
    CourseGroupListHeaderComponent,
    CourseGroupListActionsComponent,
    CourseGroupListEmptyComponent,
    RoundButtonClassPipe,
    NoDataPipe,
    DropdownComponent,
    DropdownItemComponent,
    ClickOutsideDirective,
    TabViewComponent,
    TabPanelComponent,
    SelectButtonComponent,
    InputSwitchComponent,
    InputNumberComponent,
    StudentStatListTableComponent,
    StudentPassFilterFormComponent,
    StudentPassCriteriaListComponent,
    StudentExerciseCellClassPipe,
    StudentExerciseGradePipe,
    StudentExercisePercentagePipe,
    StudentExerciseTotalPointsPipe,
    StudentExerciseGradeCellClassPipe,
    StudentExercisePercentageCellClassPipe,
    OperatorLiteralPipe,
    CourseExerciseFileUploadModalComponent,
    FileListComponent,
    ProgressBarComponent,
    HintComponent,
    FileUploadComponent,
    FormatExtensionsPipe,
    CourseExerciseAnswerTableComponent,
    CourseExerciseAnswerMenuComponent,
    ExerciseFileUploadControlButtonsComponent,
    RoundBadgeComponent,
    CourseExerciseStudentUploadComponent,
    CourseExerciseAnswerListModalComponent,
    AnswerStatusComponent,
    CardCourseComponent,
    TeacherDashboardGroupListboxElementContentComponent,
    TableComponent,
    CircleBadgeComponent,
    ShortNamePipe,
    StatusChipComponent,
    ColorStatusPipe,
    IconStatusPipe,
    ConvertCodeText,
    OrganizationSelectorComponent,
    OrganizationNamePipe,
    CourseLimitWidgetComponent,
    PageToolbarComponent,
    ExpansionPanelComponent,
    HighlightComponent,
    ArrowPositionPipe,
    PartialGradeBarComponent,
    CardCertificateComponent,
    ExercisesStatListTableComponent,
    FlagExerciseBtnComponent,
    FlaggedExercisesListComponent,
    RingProgressBarComponent,
    CourseQuizSettingsModalComponent,
    StudentFlaggedExercisesListTableComponent,
    ExerciseResolveComponent,
    BooleanToStringPipe,
    CriteriaBarComponent,
    ConfirmButtonsComponent,
    QuizInfoStudentModalComponent,
    DisableWheelDirective,
    ExerciseHelpRequestFormComponent,
    ExerciseHelpRequestActionsComponent,
    ExerciseHelpRequestModalComponent,
    ConsultationTimeComponent,
    AttendanceWidgetComponent,
    StreakBadgeComponent,
    AttendanceCalendarComponent,
    AttendanceCheckModalComponent,
    AttendanceDeleteModalComponent,
    AttendanceHistoryComponent,
    AttendanceRecordHistoryModalComponent,
    CourseGroupsComponent,
    GroupDataFormComponent,
    ScheduleModalComponent,
    DatepickerComponent,
    FileUploadCommentModalComponent,
    ExerciseTextControlButtonsComponent,
    ExerciseTextAnswerFormComponent,
    ExerciseComplexControlButtonsComponent,
    CourseExerciseAnswerHistoryModalComponent,
    ShortTextCommentModalComponent,
    CarouselBannerComponent,
    SnackbarComponent,
    DisplayNamePipe,
    DownloadFormatPanelComponent,
    RegulationsModalComponent,
    FeedbackWidgetComponent,
    RatingStarsComponent,
    FeedbackCourseModalComponent,
    CourseGradingExercisesComponent,
    NetworkStatusComponent,
    CourseBlockElementContentComponent,
    NavbarMobileComponent,
    NavbarMobileElementComponent,
    LangSwitcherComponent,
    SidebarComponent,
    CourseGroupLoadMoreComponent,
    PanelNavbarComponent,
    AuthLayoutComponent,
    SkeletonPageLoaderComponent,
    SkeletonHeaderLoaderComponent,
    SkeletonRowsLoaderComponent,
    SkeletonForumUsersLoaderComponent,
    SkeletonAttachmentButtonsLoaderComponent,
    SkeletonChatUsersLoaderComponent,
    SkeletonChatConversationLoaderComponent,
    SkeletonMoveGroupsLoaderComponent,
    UserProfileMenuComponent,
    EmptyStateComponent,
    ViewSwitcherComponent,
    CourseExerciseFileUploadDataTableComponent,
    CourseExerciseHistoryDataTableComponent,
    CourseExerciseShortTextDataTableComponent,
    CourseTeacherShortTextAnswersTableComponent,
    CourseTeacherFileUploadAnswersTableComponent,
    CourseTeacherComplexAnswersTableComponent,
    ComplexAnswersLoaderComponent,
    ExerciseFileUploadInlineFormComponent,
    SquareButtonComponent,
    MobileTextAnswerHistoryComponent,
    MobileFileUploadAnswerHistoryComponent,
    MobileQuizStartComponent,
    MobileHistoryTableComponent,
    CoursePdfReaderComponent,
  ],
  imports: [
    RouterModule,
    FormsModule,
    MonacoEditorModule,
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    ReactiveFormsModule,
    NgxMaskModule,
    OverlayModule,
    MatTooltipModule,
    CommonModule,
    LucideAngularModule.pick(LUCIDE_SHARED_ICONS),
    TablerIconsModule.pick(TABLER_SHARED_ICONS),
    CsLoaderComponent,
  ],
  exports: [
    PanelPageComponent,
    AuthLayoutComponent,
    ReactiveFormsModule,
    TranslateModule,
    NavbarComponent,
    IconCircleComponent,
    IconInputComponent,
    PageStandardLayoutComponent,
    PageColumnLayoutComponent,
    PageHeaderComponent,
    BoxComponent,
    BoxHeaderComponent,
    RoundButtonComponent,
    CourseListboxElementContentComponent,
    CourseProgressComponent,
    CourseTopicComponent,
    CourseExerciseHelpComponent,
    WarningModalComponent,
    WarningModalDirective,
    ValidationMessageComponent,
    ValidationTextComponent,
    LoadingBoxComponent,
    CsLoaderComponent,
    ReplaceNewLinePipe,
    LanguagePickerComponent,
    CountdownComponent,
    GroupInvitationListboxElementContentComponent,
    HourPickerComponent,
    PillsInputComponent,
    PillComponent,
    PillsComponent,
    SimpleListItemComponent,
    SimpleElementComponent,
    ExerciseStatusComponent,
    CourseExerciseStatComponent,
    CourseTopicStatComponent,
    HighlightDirective,
    IndicatorComponent,
    PopupMenuComponent,
    DayPipe,
    FormatHourPipe,
    ForumReactionEmojiPipe,
    ModalComponent,
    ModalOverlayComponent,
    ModalBoxComponent,
    ConfirmationModalComponent,
    ConfirmationDirective,
    InvitationStatusPipe,
    InitialsPipe,
    SanitizeContentPipe,
    IterationPipe,
    PopupDirective,
    VpnConfigurationModalComponent,
    WelcomeScreenModalComponent,
    GlobalRankingPageComponent,
    FluidTableComponent,
    RankingTableComponent,
    RankingFiltersComponent,
    RankingRefreshComponent,
    RecaptchaInfoComponent,
    ExerciseControlButtonsComponent,
    FooterComponent,
    ExerciseSolveConfirmModalComponent,
    PointsPluralizationPipe,
    PluralKeyPipe,
    StatusIconComponent,
    DropdownMenuComponent,
    CourseExerciseDescComponent,
    NavbarExerciseTimerComponent,
    QuizSolveConfirmModalComponent,
    RichTextComponent,
    CookieBarComponent,
    CodeCheckStatusPipe,
    DatePipe,
    RelativeTimePipe,
    GroupReedemListboxElementContentComponent,
    ListPickerComponent,
    SliderPickerComponent,
    ProgressRingComponent,
    PaginationComponent,
    OrderByAvailabilityPipe,
    NumberedPipe,
    AnnouncementComponent,
    SwitchButtonComponent,
    TooltipDirective,
    TooltipComponent,
    FileSizePipe,
    WidgetDirective,
    MoveStudentModalComponent,
    GroupStudentMoveListboxElementContentComponent,
    OrganizationLimitWidgetComponent,
    TileComponent,
    CarouselComponent,
    DashboardLayoutComponent,
    DashboardSidebarComponent,
    SwitchButtonExpandedComponent,
    SwitchCheckboxComponent,
    DotsComponent,
    VpnConnectionModalComponent,
    InvitationsWidgetComponent,
    CustomVideoPlayerComponent,
    ValidationHintComponent,
    EmptyStateComponent,
    LanguagePickerButtonWithFlagLayoutComponent,
    LanguagePickerLangsWithFlagLayoutComponent,
    NavbarExitExerciseButtonComponent,
    ExerciseIdeControlButtonsComponent,
    ExerciseQuizControlButtonsComponent,
    EnvironmentTranslatePipe,
    CustomVideoPlayerDirective,
    BadgeComponent,
    GroupListboxElementContentComponent,
    DividerComponent,
    SectionTitleComponent,
    ListboxComponent,
    ListboxElementComponent,
    StudentsCountBadgeComponent,
    LogoComponent,
    GroupListboxEmptyComponent,
    GroupInvitationListboxEmptyComponent,
    CourseListboxEmptyComponent,
    KeyIconComponent,
    SelectWithPlaceholderComponent,
    GroupWidgetComponent,
    IconComponent,
    CourseGroupForumCardComponent,
    CourseGroupInfoComponent,
    CourseGroupListHeaderComponent,
    CourseGroupListActionsComponent,
    CourseGroupListEmptyComponent,
    NotificationsIconComponent,
    SimpleDashboardWidgetComponent,
    HoverDirective,
    NoDataPipe,
    DropdownComponent,
    ClickOutsideDirective,
    TabPanelComponent,
    TabViewComponent,
    SelectButtonComponent,
    InputSwitchComponent,
    InputNumberComponent,
    ExerciseHistoryButtonComponent,
    StudentStatListTableComponent,
    StudentPassFilterFormComponent,
    StudentPassCriteriaListComponent,
    StudentExerciseCellClassPipe,
    StudentExerciseGradePipe,
    StudentExercisePercentagePipe,
    StudentExerciseTotalPointsPipe,
    StudentExerciseGradeCellClassPipe,
    StudentExercisePercentageCellClassPipe,
    OperatorLiteralPipe,
    ProgressBarComponent,
    HintComponent,
    CourseExerciseFileUploadModalComponent,
    FileListComponent,
    FileUploadComponent,
    FormatExtensionsPipe,
    CourseExerciseAnswerTableComponent,
    CourseExerciseAnswerComponent,
    CardCourseComponent,
    TeacherDashboardGroupListboxElementContentComponent,
    TableComponent,
    CircleBadgeComponent,
    ShortNamePipe,
    StatusChipComponent,
    ColorStatusPipe,
    IconStatusPipe,
    ConvertCodeText,
    OrganizationSelectorComponent,
    OrganizationNamePipe,
    CourseLimitWidgetComponent,
    PageToolbarComponent,
    ExpansionPanelComponent,
    HighlightComponent,
    ArrowPositionPipe,
    CardCertificateComponent,
    PartialGradeBarComponent,
    ExercisesStatListTableComponent,
    FlagExerciseBtnComponent,
    FlaggedExercisesListComponent,
    RingProgressBarComponent,
    CourseQuizSettingsModalComponent,
    StudentFlaggedExercisesListTableComponent,
    ExerciseResolveComponent,
    BooleanToStringPipe,
    CriteriaBarComponent,
    ConfirmButtonsComponent,
    QuizInfoStudentModalComponent,
    ExerciseHelpRequestFormComponent,
    ConsultationTimeComponent,
    AttendanceWidgetComponent,
    StreakBadgeComponent,
    AttendanceCalendarComponent,
    AttendanceCheckModalComponent,
    AttendanceDeleteModalComponent,
    AttendanceHistoryComponent,
    AttendanceRecordHistoryModalComponent,
    CourseGroupsComponent,
    GroupDataFormComponent,
    ScheduleModalComponent,
    DatepickerComponent,
    FileUploadCommentModalComponent,
    CarouselBannerComponent,
    SnackbarComponent,
    DisplayNamePipe,
    DownloadFormatPanelComponent,
    RegulationsModalComponent,
    FeedbackWidgetComponent,
    CourseGradingExercisesComponent,
    NetworkStatusComponent,
    CourseBlockElementContentComponent,
    MatTooltipModule,
    LangSwitcherComponent,
    SidebarComponent,
    CourseGroupLoadMoreComponent,
    PanelNavbarComponent,
    NavbarMobileComponent,
    SkeletonPageLoaderComponent,
    SkeletonHeaderLoaderComponent,
    SkeletonRowsLoaderComponent,
    SkeletonForumUsersLoaderComponent,
    SkeletonAttachmentButtonsLoaderComponent,
    SkeletonChatUsersLoaderComponent,
    SkeletonChatConversationLoaderComponent,
    SkeletonMoveGroupsLoaderComponent,
    ListProgressComponent,
    UserProfileMenuComponent,
    ViewSwitcherComponent,
    SquareButtonComponent,
    CoursePdfReaderComponent,
  ],
  providers: [OrderByAvailabilityPipe, ExerciseStatusCssClassPipe],
  bootstrap: [],
})
export class SharedModule {}
